import axios, { AxiosResponse } from 'axios';
import { toast } from 'sonner';
import i18next from 'i18next';
import { BaseErrorSchema, ErrorCode } from '@bliks-io/contracts';
import { env } from '@/config/env';
import { authKeys } from '@/api/query-keys';
import { getApiErrorCode, isForbiddenError } from '@/api/errors';
import { translateErrorCode } from '@/lib/errors/error-formatter';
import { queryClient } from '@/lib/query-client';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { ROUTES } from '@/constants/routes';
import { getRegisteredRouter } from '@/lib/router-registry';
import { useAuthStore } from '@/stores/auth-store';
import { logger } from './logger';
import { refreshSession } from './auth/refreshSession';
import { teardownSessionToLogin } from './auth/sessionTeardown';
import type {
  ExtendedAxiosRequestConfig,
  ExtendedAxiosError,
  ApiError,
  AgentConfig,
  PerformanceCallback,
  RequestMetrics
} from './agent.types';

/** Replays attempted after the initial request, before giving up. */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Default agent configuration
 */
const DEFAULT_CONFIG: AgentConfig = {
  timeout: 30000, // 30 seconds
  enableLogging: env.DEV,
  enableRequestId: true,
  enablePerformanceMonitoring: true,
  retry: {
    maxRetries: MAX_RETRY_ATTEMPTS,
    delayStrategy: 'exponential',
    baseDelay: 1000,
    retryableErrorCodes: ['ECONNABORTED', 'ETIMEDOUT', 'ENETUNREACH']
  }
};

/** Methods safe to replay. A timed-out POST may still be executing
 *  server-side, so non-idempotent verbs are never retried. */
const RETRYABLE_METHODS: readonly string[] = ['get', 'head', 'options'];

// Configure logger based on config
logger.setEnabled(DEFAULT_CONFIG.enableLogging);

/**
 * Performance monitoring callbacks
 */
const performanceCallbacks: PerformanceCallback[] = [];

/**
 * Register a performance monitoring callback
 */
export const onRequestComplete = (callback: PerformanceCallback): (() => void) => {
  performanceCallbacks.push(callback);
  return () => {
    const index = performanceCallbacks.indexOf(callback);
    if (index > -1) performanceCallbacks.splice(index, 1);
  };
};

/**
 * Generate unique request ID
 */
const generateRequestId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Calculate retry delay based on strategy
 */
const getRetryDelay = (retryCount: number, config: AgentConfig['retry']): number => {
  switch (config.delayStrategy) {
    case 'exponential':
      return config.baseDelay * Math.pow(2, retryCount - 1);
    case 'linear':
      return config.baseDelay * retryCount;
    case 'constant':
      return config.baseDelay;
    default:
      return config.baseDelay;
  }
};

/**
 * Check if error is retryable
 */
const isRetryableError = (
  error: ExtendedAxiosError,
  retryConfig: AgentConfig['retry']
): boolean => {
  // Axios lowercases `method` when it dispatches, but this gate is a safety
  // property — it must not silently depend on that internal staying true.
  const method = error.config?.method?.toLowerCase();
  if (!method || !RETRYABLE_METHODS.includes(method)) {
    return false;
  }

  // Never retry cancelled requests (from AbortController/signal)
  if (axios.isCancel(error)) {
    return false;
  }

  // Network errors or timeout
  if (error.code && retryConfig.retryableErrorCodes?.includes(error.code)) {
    return true;
  }

  // Server errors (5xx)
  if (error.response && error.response.status >= 500) {
    return true;
  }

  // No response (network error)
  if (!error.response) {
    return true;
  }

  return false;
};

/**
 * Log request in development mode
 */
const logRequest = (config: ExtendedAxiosRequestConfig): void => {
  logger.group(`🚀 ${config.method?.toUpperCase()} ${config.url} [${config.__requestId}]`);
  if (config.params) logger.log('Params:', config.params);
  if (config.data) logger.log('Data:', config.data);
  logger.groupEnd();
};

/**
 * Log response in development mode
 */
const logResponse = (
  response: AxiosResponse,
  config: ExtendedAxiosRequestConfig,
  duration: number
): void => {
  logger.group(
    `✅ ${config.method?.toUpperCase()} ${config.url} [${config.__requestId}] - ${duration}ms`
  );
  logger.log('Status:', response.status);
  logger.log('Data:', response.data);
  logger.groupEnd();
};

/**
 * Log error in development mode
 */
const logError = (
  error: ExtendedAxiosError,
  config: ExtendedAxiosRequestConfig,
  duration?: number
): void => {
  const durationStr = duration ? ` - ${duration}ms` : '';
  logger.group(
    `❌ ${config.method?.toUpperCase()} ${config.url} [${config.__requestId}]${durationStr}`
  );
  logger.error('Status:', error.response?.status);
  logger.error('Message:', error.message);
  if (error.response?.data) logger.error('Data:', error.response.data);
  logger.groupEnd();
};

/**
 * Report performance metrics
 */
const reportMetrics = (metrics: RequestMetrics): void => {
  if (!DEFAULT_CONFIG.enablePerformanceMonitoring) return;

  performanceCallbacks.forEach((callback) => {
    try {
      callback(metrics);
    } catch (error) {
      logger.error('Error in performance callback:', error);
    }
  });
};

/**
 * Handle retry logic for failed requests
 */
const handleRetry = async (
  error: ExtendedAxiosError,
  config: ExtendedAxiosRequestConfig
): Promise<AxiosResponse | null> => {
  config.__retryCount = config.__retryCount || 0;

  const shouldRetry =
    !config.skipRetry &&
    config.__retryCount < DEFAULT_CONFIG.retry.maxRetries &&
    isRetryableError(error, DEFAULT_CONFIG.retry);

  if (!shouldRetry) {
    return null;
  }

  config.__retryCount += 1;
  const delay = getRetryDelay(config.__retryCount, DEFAULT_CONFIG.retry);

  logger.warn(
    `⚠️ Retrying ${config.method?.toUpperCase()} ${config.url} [${config.__requestId}] (${config.__retryCount}/${DEFAULT_CONFIG.retry.maxRetries}) after ${delay}ms`
  );

  await new Promise((resolve) => setTimeout(resolve, delay));
  return agent(config);
};

// Auth-flow pages render their own 401/403s — the global handlers must not
// redirect or toast over them, or the user never sees the message. During
// logout, in-flight requests fail the moment the token clears — a redirect
// or toast here would race the logout's own navigation.
const AUTH_FLOW_ERROR_ROUTES: readonly string[] = [ROUTES.LOGIN, ROUTES.SSO_CALLBACK];

// Before the router exists the browser's own location is the current page.
const currentPathname = (): string =>
  getRegisteredRouter()?.state.location.pathname ?? window.location.pathname;

const shouldSuppressGlobalAuthHandling = (): boolean =>
  AUTH_FLOW_ERROR_ROUTES.includes(currentPathname()) || useAuthStore.getState().isLoggingOut;

/**
 * Owns the entire 401 branch: renew, replay, or tear down.
 *
 * An access token lives an hour and nothing refreshed it, so every session
 * used to die mid-work — the user landed on an empty login form and lost the
 * page they were on. Renewal happens here, one layer below the application:
 * queries never learn that their token expired.
 *
 * The three outcomes are deliberately distinct. Only the server refusing the
 * SESSION tears down; a 429 from the shared per-IP limit, a 503 while the
 * identity provider restarts, or a dropped connection must leave the session
 * alone and just fail this one request — logging a user out because a packet
 * went missing is worse than the error they would have seen.
 *
 * Answers a response when the request was replayed, null when the caller
 * should reject as usual. `__refreshRetried` bounds it to one attempt per
 * request, so a token the server keeps rejecting cannot loop.
 */
const handleUnauthorized = async (
  error: ExtendedAxiosError,
  config: ExtendedAxiosRequestConfig
): Promise<AxiosResponse | null> => {
  if (error.response?.status !== 401) return null;
  if (config.__refreshRetried) return null;
  if (shouldSuppressGlobalAuthHandling()) return null;

  config.__refreshRetried = true;

  // A straggler whose 401 landed after another request already renewed: its
  // Authorization header is stale, not the session. Replay it directly rather
  // than paying for a second rotation at the identity provider.
  if (hasStaleAuthorization(config)) {
    return agent(config);
  }

  const outcome = await refreshSession();
  if (outcome === 'renewed') {
    logger.warn(`🔄 Session renewed - replaying ${config.method?.toUpperCase()} ${config.url}`);
    return agent(config);
  }

  if (outcome === 'dead') {
    logger.warn('🔒 401 Unauthorized - clearing auth and redirecting to login');
    teardownSessionToLogin();
  }

  return null;
};

const hasStaleAuthorization = (config: ExtendedAxiosRequestConfig): boolean => {
  const sent = config.headers?.get?.('Authorization');
  const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  return Boolean(stored) && sent !== `Bearer ${stored}`;
};

/**
 * The only toast a 403 gets — callers never toast one. A caller with its own
 * 403 message opts out with `suppressForbiddenToast`.
 */
const handle403Forbidden = (enriched: ApiError, config: ExtendedAxiosRequestConfig): void => {
  if (!isForbiddenError(enriched) || shouldSuppressGlobalAuthHandling()) return;

  const code = getApiErrorCode(enriched);

  // Refetching the profile moves the open session onto the lock screen (see
  // SlugLayout). Checked before suppressForbiddenToast, which opts a request
  // out of the toast, not out of the lock.
  if (code === ErrorCode.SEAT_OVERRUN) {
    void queryClient.invalidateQueries(
      { queryKey: authKeys.profile() },
      // A burst of blocked writes shares one in-flight refetch.
      { cancelRefetch: false }
    );
    return;
  }

  // The step-up dialog (handleSensitiveMutationError) is the message.
  if (code === ErrorCode.AUTH_REAUTH_REQUIRED) return;

  if (config.suppressForbiddenToast) return;
  logger.warn('🚫 403 Forbidden');
  toast.error(translateErrorCode(enriched, 'errors.insufficientPermissions', i18next.t));
};

const BASE_ERROR_KEYS: ReadonlySet<string> = new Set(Object.keys(BaseErrorSchema.shape));

/** A JSON error body, or undefined for anything else the server may have sent
 *  (HTML, a bare string, an array) — none of which carries named fields. */
const asObjectBody = (raw: unknown): Record<string, unknown> | undefined =>
  typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : undefined;

/** Body fields the contract does not own, so a failed parse can still hand
 *  them to the call site that reads them. */
const extensionKeys = (body: Record<string, unknown> | undefined): Record<string, unknown> =>
  body ? Object.fromEntries(Object.entries(body).filter(([key]) => !BASE_ERROR_KEYS.has(key))) : {};

/**
 * Enrich API error with standardized structure
 *
 * Extra response-body fields (e.g. `contentErrors`/`graphErrors` from
 * publish-draft validation) are spread through unchanged so call sites
 * can read them directly off the enriched error. The explicit fields
 * below stay authoritative — they overwrite the spread.
 */
const enrichApiError = (
  error: ExtendedAxiosError,
  config: ExtendedAxiosRequestConfig
): ApiError => {
  const raw = error.response!.data;
  const statusCode = error.response!.status;
  const objectBody = asObjectBody(raw);
  // The HTTP status outranks a body that disagrees with it, same rule the
  // backend filter applies — the 403/404/409 classifiers read this field. It
  // is also what lets a body from a backend that omits `statusCode` parse.
  const parsed = BaseErrorSchema.safeParse(objectBody ? { ...objectBody, statusCode } : raw);
  const statusError = error.response!.statusText || 'Unknown Error';
  const origin = {
    statusCode,
    requestId: config.__requestId,
    timestamp: new Date().toISOString(),
    path: config.url
  };

  // A body the contract rejects carries none of its contract-owned fields
  // through — but its extension keys are still the only copy call sites have.
  if (!parsed.success) {
    logger.warn('Unrecognised API error body', parsed.error.issues);
    const bodyMessage = objectBody?.message;
    return {
      ...extensionKeys(objectBody),
      ...origin,
      message: typeof bodyMessage === 'string' ? bodyMessage : error.message,
      error: statusError
    };
  }

  return {
    ...(raw as object),
    ...parsed.data,
    ...origin,
    error: parsed.data.error ?? statusError
  };
};

/**
 * Enrich network error with standardized structure
 */
const enrichNetworkError = (
  _error: ExtendedAxiosError,
  config: ExtendedAxiosRequestConfig
): ApiError => {
  return {
    message: 'errors.network',
    statusCode: 0,
    error: 'Network Error',
    requestId: config.__requestId,
    timestamp: new Date().toISOString(),
    path: config.url
  };
};

/**
 * V2 API Agent
 *
 * Enhanced axios instance configured for V2 backend communication.
 *
 * Features:
 * - Automatic auth token injection from v2 localStorage
 * - Request ID tracking for debugging
 * - Retry with exponential backoff, limited to idempotent methods
 *   (GET/HEAD/OPTIONS) — see RETRYABLE_METHODS
 * - 401 error handling via auth store
 * - Error enrichment with status codes
 * - Performance monitoring
 * - Development mode logging
 * - Request timeout (30s default)
 */
const agent = axios.create({
  baseURL: env.BACKEND_ENDPOINT,
  timeout: DEFAULT_CONFIG.timeout,
  // The browser must store the SSO transaction cookie from /auth/sso/start and
  // /step-up and send it back on /callback. In deployed same-origin this is a
  // no-op; in local cross-origin (same-site) dev it is required. Auth itself
  // stays bearer-token based.
  withCredentials: true
});

// ============================================================================
// Request Interceptor
// ============================================================================
agent.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    // Add request ID for tracking
    if (DEFAULT_CONFIG.enableRequestId) {
      config.__requestId = generateRequestId();
      config.headers.set('X-Request-ID', config.__requestId);
    }

    // Add start time for performance tracking
    if (DEFAULT_CONFIG.enablePerformanceMonitoring) {
      config.__startTime = Date.now();
    }

    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    // Log request in dev mode
    logRequest(config);

    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// Response Interceptor
// ============================================================================
agent.interceptors.response.use(
  // Success handler
  (response: AxiosResponse) => {
    const config = response.config as ExtendedAxiosRequestConfig;
    const duration = config.__startTime ? Date.now() - config.__startTime : 0;

    // Log response in dev mode
    logResponse(response, config, duration);

    // Report metrics
    if (DEFAULT_CONFIG.enablePerformanceMonitoring && config.__requestId) {
      reportMetrics({
        requestId: config.__requestId,
        method: config.method?.toUpperCase() || 'UNKNOWN',
        url: config.url || 'unknown',
        duration,
        statusCode: response.status,
        success: true,
        retryCount: config.__retryCount || 0
      });
    }

    return response;
  },

  // Error handler
  async (error: ExtendedAxiosError) => {
    // A request-interceptor rejection carries no dispatched config. Reading
    // through it unguarded throws a TypeError that replaces the real error —
    // and would short-circuit the retry gate below before it can run.
    const config = (error.config ?? {}) as ExtendedAxiosRequestConfig;
    const duration = config.__startTime ? Date.now() - config.__startTime : undefined;

    // Attempt retry
    const retryResponse = await handleRetry(error, config);
    if (retryResponse) {
      return retryResponse;
    }

    // An expired token is a renewable session, not a dead one. This owns the
    // whole 401 branch, teardown included.
    const renewedResponse = await handleUnauthorized(error, config);
    if (renewedResponse) {
      return renewedResponse;
    }

    // Log error in dev mode
    logError(error, config, duration);

    // Report metrics
    if (DEFAULT_CONFIG.enablePerformanceMonitoring && config.__requestId) {
      reportMetrics({
        requestId: config.__requestId,
        method: config.method?.toUpperCase() || 'UNKNOWN',
        url: config.url || 'unknown',
        duration: duration || 0,
        statusCode: error.response?.status,
        success: false,
        retryCount: config.__retryCount || 0
      });
    }

    // Enrich first: handle403Forbidden classifies the parsed body, not the raw one.
    const enrichedError = error.response
      ? enrichApiError(error, config)
      : enrichNetworkError(error, config);

    handle403Forbidden(enrichedError, config);

    return Promise.reject(enrichedError);
  }
);

export default agent;
